import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { NavLink, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  BarChart3,
  Wallet,
  Settings,
  X,
  Ticket,
  Star,
  FolderTree,
  Warehouse,
  Languages,
  Store,
  ExternalLink,
  Truck,
  Blocks,
  UsersRound,
  Megaphone,
  LinkIcon,
  ShoppingBasket,
  Link2,
  Mail,
  MessageSquare,
  AlertCircle,
  PenLine,
  Search,
  Radio,
  Receipt,
  Menu,
  FlaskConical,
  Shield,
  Paintbrush,
  ArrowUpRight,
  Share2,
  Copy,
  Check,
  ShoppingBag,
  Image as ImageIcon,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useStore } from '../../contexts/StoreContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getStoreLogoUrl } from '../../lib/formatters';
import { PERMISSIONS } from '../../lib/permissions';
import { ordersApi } from '../../api/endpoints/orders';
import { authApi } from '../../api/endpoints/auth';
import { productsApi } from '../../api/endpoints/products';
import { customersApi } from '../../api/endpoints/customers';
import { couponsApi } from '../../api/endpoints/coupons';
import { analyticsApi } from '../../api/endpoints/analytics';
import { walletApi } from '../../api/endpoints/wallet';
import { reviewsApi } from '../../api/endpoints/reviews';
import { cartsApi } from '../../api/endpoints/carts';
import { ZeroLogo } from './ZeroLogo';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  id: string;
  path: string;
  Icon: LucideIcon;
  labelKey: string;
  permission?: string;
  badge?: 'unseen-orders';
}

interface NavSection {
  labelKey?: string;
  labelFallback?: string;
  items: NavItem[];
}

const PRODUCTS_SUB: NavItem[] = [
  { id: 'categories', path: '/categories', Icon: FolderTree, labelKey: 'sidebar.categories' },
  { id: 'inventory', path: '/inventory', Icon: Warehouse, labelKey: 'sidebar.inventory' },
  { id: 'translations', path: '/translations', Icon: Languages, labelKey: 'sidebar.translations' },
];

const MARKETING_SUB: NavItem[] = [
  { id: 'coupons', path: '/coupons', Icon: Ticket, labelKey: 'sidebar.coupons' },
  { id: 'reviews', path: '/reviews', Icon: Star, labelKey: 'sidebar.reviews' },
  { id: 'analytical-links', path: '/analytical-links', Icon: LinkIcon, labelKey: 'sidebar.analyticalLinks' },
  { id: 'carts', path: '/carts', Icon: ShoppingBasket, labelKey: 'sidebar.carts' },
  { id: 'newsletter', path: '/newsletter', Icon: Mail, labelKey: 'sidebar.newsletter' },
];

const ANALYTICS_SUB: NavItem[] = [
  { id: 'search-analytics', path: '/analytics/search', Icon: Search, labelKey: 'sidebar.searchAnalytics' },
  { id: 'live-visitors', path: '/analytics/live', Icon: Radio, labelKey: 'sidebar.liveVisitors' },
  { id: 'ab-testing', path: '/ab-testing', Icon: FlaskConical, labelKey: 'sidebar.abTesting' },
];

const DESIGN_SUB: NavItem[] = [
  { id: 'blog', path: '/blog', Icon: PenLine, labelKey: 'sidebar.blog' },
  { id: 'menus', path: '/menus', Icon: Menu, labelKey: 'sidebar.menus' },
];

const SETTINGS_SUB: NavItem[] = [
  { id: 'shipping', path: '/shipping', Icon: Truck, labelKey: 'sidebar.shipping' },
  { id: 'apps', path: '/apps', Icon: Blocks, labelKey: 'sidebar.apps' },
  { id: 'team', path: '/users', Icon: UsersRound, labelKey: 'sidebar.team' },
  { id: 'expenses', path: '/expenses', Icon: Receipt, labelKey: 'sidebar.expenses' },
  { id: 'contacts', path: '/contacts', Icon: MessageSquare, labelKey: 'sidebar.contactInbox' },
  { id: 'complaints', path: '/complaints', Icon: AlertCircle, labelKey: 'sidebar.complaints' },
  { id: 'verification', path: '/verification', Icon: Shield, labelKey: 'sidebar.verification' },
];

/** Two logical groupings - الإدارة (run the store) & المتجر (shape the store) */
const SECTIONS: NavSection[] = [
  {
    labelFallback: 'الإدارة',
    items: [
      { id: 'home', path: '', Icon: LayoutDashboard, labelKey: 'sidebar.home' },
      { id: 'orders', path: '/orders', Icon: ShoppingCart, labelKey: 'sidebar.orders', permission: PERMISSIONS.ORDERS_VIEW, badge: 'unseen-orders' },
      { id: 'products', path: '/products', Icon: Package, labelKey: 'sidebar.products', permission: PERMISSIONS.PRODUCTS_VIEW },
      { id: 'customers', path: '/customers', Icon: Users, labelKey: 'sidebar.customers', permission: PERMISSIONS.CUSTOMERS_VIEW },
      { id: 'paylinks', path: '/paylinks', Icon: Link2, labelKey: 'sidebar.paylinks' },
      { id: 'wallet', path: '/wallet', Icon: Wallet, labelKey: 'sidebar.wallet' },
      { id: 'analytics', path: '/analytics', Icon: BarChart3, labelKey: 'sidebar.analytics' },
    ],
  },
  {
    labelFallback: 'المتجر',
    items: [
      { id: 'marketing', path: '/marketing', Icon: Megaphone, labelKey: 'sidebar.marketing' },
      { id: 'design', path: '/design', Icon: Paintbrush, labelKey: 'sidebar.storeDesign' },
      { id: 'settings', path: '/settings', Icon: Settings, labelKey: 'sidebar.storeSettings' },
    ],
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentStore, permissions, role } = useStore();
  const { can } = usePermissions();

  const base = `/${storeId}`;
  const isRtl = document.documentElement.dir === 'rtl';

  const permissionsLoaded = role != null || (permissions && permissions.length > 0);
  const canSee = (p?: string) => !p || !permissionsLoaded || can(p);

  const productsPaths = ['/products', '/categories', '/inventory', '/translations'];
  const showProductsSub = productsPaths.some(p => location.pathname.includes(`/${storeId}${p}`));

  const analyticsPaths = ['/analytics', '/ab-testing'];
  const showAnalyticsSub = analyticsPaths.some(p => location.pathname.includes(`/${storeId}${p}`));

  const marketingPaths = ['/marketing', '/coupons', '/reviews', '/analytical-links', '/carts', '/newsletter'];
  const showMarketingSub = marketingPaths.some(p => location.pathname.includes(`/${storeId}${p}`));

  const designPaths = ['/design', '/blog', '/menus'];
  const showDesignSub = designPaths.some(p => location.pathname.includes(`/${storeId}${p}`));

  const settingsPaths = ['/settings', '/shipping', '/apps', '/users', '/expenses', '/contacts', '/complaints', '/verification'];
  const showSettingsSub = settingsPaths.some(p => location.pathname.includes(`/${storeId}${p}`));

  const { data: unseenCount } = useQuery({
    queryKey: ['orders-unseen', storeId],
    queryFn: () => ordersApi.unseenCount(storeId!),
    enabled: !!storeId,
    refetchInterval: 30000,
  });

  const storeUrl = currentStore?.domain
    ? `https://${currentStore.domain}`
    : storeId
      ? `https://${storeId}.00stores.com`
      : null;

  const queryClient = useQueryClient();
  const prefetchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const prefetchMap: Record<string, (() => void) | undefined> = useMemo(() => {
    if (!storeId) return {};
    return {
      '/orders': () => queryClient.prefetchQuery({ queryKey: ['orders', storeId, { page: 1, limit: 20 }], queryFn: () => ordersApi.list(storeId, { page: 1, limit: 20 }), staleTime: 30_000 }),
      '/products': () => queryClient.prefetchQuery({ queryKey: ['products', storeId, { page: 1, limit: 20 }], queryFn: () => productsApi.list(storeId, { page: 1, limit: 20 }), staleTime: 30_000 }),
      '/customers': () => queryClient.prefetchQuery({ queryKey: ['customers', storeId, { page: 1, limit: 20 }], queryFn: () => customersApi.list(storeId, { page: 1, limit: 20 }), staleTime: 30_000 }),
      '/analytics': () => queryClient.prefetchQuery({ queryKey: ['analytics', storeId, '30d'], queryFn: () => analyticsApi.getAnalytics(storeId, '30d'), staleTime: 60_000 }),
      '/wallet': () => queryClient.prefetchQuery({ queryKey: ['wallet', storeId], queryFn: () => walletApi.getOverview(storeId), staleTime: 30_000 }),
      '/coupons': () => queryClient.prefetchQuery({ queryKey: ['coupons', storeId], queryFn: () => couponsApi.list(storeId), staleTime: 30_000 }),
      '/reviews': () => queryClient.prefetchQuery({ queryKey: ['reviews', storeId, { page: 1, limit: 20 }], queryFn: () => reviewsApi.list(storeId, { page: 1, limit: 20 }), staleTime: 30_000 }),
      '': () => queryClient.prefetchQuery({ queryKey: ['widgets', storeId], queryFn: () => analyticsApi.getWidgets(storeId), staleTime: 30_000 }),
    };
  }, [storeId, queryClient]);

  const handlePrefetch = useCallback((itemPath: string) => {
    const fn = prefetchMap[itemPath];
    if (!fn) return;
    prefetchTimers.current[itemPath] = setTimeout(fn, 100);
  }, [prefetchMap]);

  const cancelPrefetch = useCallback((itemPath: string) => {
    const timer = prefetchTimers.current[itemPath];
    if (timer) { clearTimeout(timer); delete prefetchTimers.current[itemPath]; }
  }, []);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={onClose} />
      )}

      <aside
        className={clsx(
          'fixed top-0 start-0 z-50 flex h-full w-60 2xl:w-64 flex-col bg-bg-sidebar transition-transform duration-200 lg:static lg:translate-x-0',
          'border-e border-border-default',
          isOpen ? 'translate-x-0' : isRtl ? 'translate-x-full' : '-translate-x-full',
        )}
        // On native the sidebar is `fixed top-0` and the viewport extends
        // under the status bar / home indicator (viewport-fit=cover), so its
        // content otherwise tucks under both. env() is 0 on desktop, so
        // this is a no-op there.
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <button
          onClick={onClose}
          className="absolute end-3 lg:hidden text-text-muted hover:text-text-primary p-1"
          style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
        >
          <X className="h-4 w-4" />
        </button>

        {/* ── Platform logo ── */}
        <div className="px-5 pt-5 pb-4">
          <button
            onClick={() => navigate('/')}
            className="text-text-primary hover:text-accent-pop transition-colors"
          >
            <ZeroLogo className="h-6 2xl:h-7 w-auto" />
          </button>
        </div>

        {/* ── Store identity + status chip ── */}
        <div className="px-3 pb-4">
          <div
            onClick={() => storeUrl && window.open(storeUrl, '_blank')}
            className={clsx(
              'flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors',
              'bg-bg-secondary border border-border-default',
              storeUrl && 'cursor-pointer hover:border-border-strong',
            )}
          >
            {currentStore?.logo ? (
              <img src={getStoreLogoUrl(currentStore.logo, 64)} alt="" className="h-9 w-9 shrink-0 rounded-xl object-cover" />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold"
                   style={{ background: '#D2E9FE', color: '#0A2248' }}>
                {currentStore?.name?.charAt(0) ?? '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-text-primary leading-tight">{currentStore?.name ?? '-'}</div>
              {currentStore?.testing ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${base}/verification`);
                    onClose();
                  }}
                  className="pill mt-1 cursor-pointer transition-opacity hover:opacity-80"
                  style={{
                    padding: '2px 8px',
                    fontSize: 10,
                    background: 'rgba(251,191,36,0.18)',
                    color: '#fbbf24',
                    border: '1px solid rgba(251,191,36,0.35)',
                  }}
                >
                  {t('sidebar.testingStore', 'وضع التجربة')}
                </button>
              ) : (
                <span className="pill pill-success mt-1" style={{ padding: '2px 8px', fontSize: 10 }}>
                  {t('sidebar.liveStore', 'مفعّل')}
                </span>
              )}
            </div>
            {storeUrl && (
              <ArrowUpRight className="h-4 w-4 shrink-0 text-text-muted" />
            )}
          </div>
        </div>

        {/* ── Nav ── */}
        <nav className="flex-1 overflow-y-auto px-3">
          {SECTIONS.map((section, si) => {
            const items = section.items.filter(i => canSee(i.permission));
            if (!items.length) return null;

            return (
              <div key={si} className="mb-5">
                {(section.labelKey || section.labelFallback) && (
                  <div className="px-3 mb-2 text-[10.5px] text-text-muted font-semibold uppercase tracking-[0.14em]">
                    {section.labelKey ? t(section.labelKey) : section.labelFallback}
                  </div>
                )}
                <div className="space-y-0.5">
                  {items.map(item => (
                    <div key={item.id}>
                      <NavLink
                        to={`${base}${item.path}`}
                        end={item.path === ''}
                        onClick={onClose}
                        onMouseEnter={() => handlePrefetch(item.path)}
                        onMouseLeave={() => cancelPrefetch(item.path)}
                        className={({ isActive }) =>
                          clsx(
                            'group flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] 2xl:text-sm transition-colors',
                            isActive
                              ? 'font-semibold'
                              : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
                          )
                        }
                        style={({ isActive }) => isActive
                          ? { background: '#D2E9FE', color: '#0A2248' }
                          : undefined
                        }
                      >
                        <item.Icon className="h-[17px] w-[17px] 2xl:h-[18px] 2xl:w-[18px] shrink-0" />
                        <span className="flex-1">{t(item.labelKey)}</span>
                        {item.badge === 'unseen-orders' && unseenCount != null && unseenCount > 0 && (
                          <NavBadge count={unseenCount} />
                        )}
                      </NavLink>

                      {item.id === 'products' && showProductsSub && (
                        <SubMenu items={PRODUCTS_SUB} base={base} onClose={onClose} />
                      )}
                      {item.id === 'analytics' && showAnalyticsSub && (
                        <SubMenu items={ANALYTICS_SUB} base={base} onClose={onClose} />
                      )}
                      {item.id === 'marketing' && showMarketingSub && (
                        <SubMenu items={MARKETING_SUB} base={base} onClose={onClose} />
                      )}
                      {item.id === 'design' && showDesignSub && (
                        <SubMenu items={DESIGN_SUB} base={base} onClose={onClose} />
                      )}
                      {item.id === 'settings' && showSettingsSub && (
                        <SubMenu items={SETTINGS_SUB} base={base} onClose={onClose} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* ── Testing-mode CTA: surface the verification page so merchants
                in testing mode can find it without expanding the Settings
                sub-menu. ── */}
          {currentStore?.testing && (
            <div className="mb-5">
              <NavLink
                to={`${base}/verification`}
                onClick={onClose}
                className="flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] 2xl:text-sm font-semibold transition-colors"
                style={{
                  background: 'rgba(251,191,36,0.10)',
                  color: '#fbbf24',
                  border: '1px solid rgba(251,191,36,0.30)',
                }}
              >
                <Shield className="h-[17px] w-[17px] 2xl:h-[18px] 2xl:w-[18px] shrink-0" />
                <span className="flex-1">{t('sidebar.verifyStore', 'وثّق متجرك')}</span>
              </NavLink>
            </div>
          )}

          {/* ── Smart next-step card (falls back to share-store) ── */}
          <div className="mb-5">
            <SmartNextStep storeId={storeId} storeUrl={storeUrl} base={base} onClose={onClose} />
          </div>
        </nav>

        {/* ── Bottom - switch store ── */}
        <div className="border-t border-border-default px-3 pt-3 pb-3">
          <button
            onClick={() => { navigate('/'); onClose(); }}
            className="flex w-full items-center gap-3 px-3 py-2 2xl:py-2.5 rounded-xl text-[13px] 2xl:text-sm text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
          >
            <Store className="h-[17px] w-[17px] 2xl:h-[18px] 2xl:w-[18px] shrink-0" />
            <span>{t('sidebar.switchStore')}</span>
            {storeUrl && (
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ms-auto shrink-0 text-text-muted hover:text-accent-pop"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

function NavBadge({ count }: { count: number }) {
  return (
    <span
      className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-bold num"
      style={{ background: '#D2E9FE', color: '#0A2248' }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SubMenu({ items, base, onClose }: { items: NavItem[]; base: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="ms-7 2xl:ms-8 mt-1 mb-1 space-y-0.5 relative">
      <span className="absolute inset-y-1 start-0 w-px bg-border-default/70" />
      {items.map(sub => (
        <NavLink
          key={sub.id}
          to={`${base}${sub.path}`}
          onClick={onClose}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-2 ps-3 pe-2 py-1.5 2xl:py-[7px] rounded-lg text-[12px] 2xl:text-[13px] transition-colors',
              isActive ? 'text-accent-pop font-semibold' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover',
            )
          }
        >
          <sub.Icon className="h-3.5 w-3.5 2xl:h-4 2xl:w-4 shrink-0" />
          <span>{t(sub.labelKey)}</span>
        </NavLink>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Smart next-step - picks the most useful action right now.
   Priority ladder (first match wins):
     1. unfulfilled orders
     2. low-stock products
     3. abandoned carts with value
     4. no products
     5. no logo
     → fallback: share-store
   ════════════════════════════════════════════════════════════ */

/** Step kinds the merchant can toggle in the pen-icon preferences panel.
 *  Keep ids in sync with the steps pushed below. */
const NEXT_STEP_KINDS: ReadonlyArray<{ id: string; labelKey: string; fallback: string }> = [
  { id: 'unfulfilled',  labelKey: 'nextStep.kinds.unfulfilled',  fallback: 'طلبات تنتظر التجهيز' },
  { id: 'low-stock',    labelKey: 'nextStep.kinds.lowStock',     fallback: 'مخزون على وشك النفاد' },
  { id: 'carts',        labelKey: 'nextStep.kinds.carts',        fallback: 'سلات مهجورة' },
  { id: 'no-products',  labelKey: 'nextStep.kinds.noProducts',   fallback: 'إضافة أول منتج' },
  { id: 'no-logo',      labelKey: 'nextStep.kinds.noLogo',       fallback: 'إضافة شعار المتجر' },
];

function SmartNextStep({ storeId, storeUrl, base, onClose }:
{ storeId: string | undefined; storeUrl: string | null; base: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentStore } = useStore();
  const { user, refreshUser, isDemo } = useAuth();
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Local mirror of the account-level pref so the toggle feels instant; we
  // still PATCH the server and re-sync via refreshUser on success.
  const [hidden, setHidden] = useState<string[]>(user?.preferences?.hiddenNextSteps ?? []);
  useEffect(() => {
    setHidden(user?.preferences?.hiddenNextSteps ?? []);
  }, [user?.preferences?.hiddenNextSteps]);

  const toggleHidden = async (id: string) => {
    const next = hidden.includes(id) ? hidden.filter(x => x !== id) : [...hidden, id];
    setHidden(next);
    if (isDemo) return; // demo mode: no backend round-trip
    try {
      await authApi.updatePreferences({ hiddenNextSteps: next });
      await refreshUser();
    } catch {
      // Roll back local state if the server rejected the patch.
      setHidden(hidden);
    }
  };

  const { data: widgets } = useQuery({
    queryKey: ['dashboard-widgets', storeId],
    queryFn: () => analyticsApi.getWidgets(storeId!).catch(() => null),
    enabled: !!storeId,
    staleTime: 60_000,
    retry: false,
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-count', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 1 }),
    enabled: !!storeId,
    staleTime: 60_000,
  });

  const { data: orderCounts } = useQuery({
    queryKey: ['orders-status-counts', storeId],
    queryFn: () => ordersApi.statusCounts(storeId!).catch(() => ({} as Record<string, number>)),
    enabled: !!storeId,
    staleTime: 60_000,
    retry: false,
  });

  const { data: cartData } = useQuery({
    queryKey: ['dashboard-carts', storeId],
    queryFn: () => cartsApi.list(storeId!, { limit: 1 }).catch(() => null),
    enabled: !!storeId,
    staleTime: 60_000,
    retry: false,
  });

  const go = (path: string) => { navigate(`${base}${path}`); onClose(); };

  const unfulfilled =
    (orderCounts?.pending ?? 0) +
    (orderCounts?.processing ?? 0) +
    (orderCounts?.confirming ?? 0) +
    (orderCounts?.confirmed ?? 0);

  const lowStock = widgets?.lowStockProducts?.length ?? 0;
  const cartStats = cartData?.stats;
  const cartCount = cartStats?.totalCarts ?? 0;
  const cartValue = cartStats?.totalValue ?? 0;
  const productCount = productsData?.total ?? productsData?.products?.length ?? 0;
  const hasLogo = !!currentStore?.logo;
  // Sum every status bucket — once a store has its first sale we drop the
  // "first order ever" copy on the share-store fallback.
  const totalOrders = orderCounts
    ? Object.values(orderCounts).reduce((sum, n) => sum + (typeof n === 'number' ? n : 0), 0)
    : 0;
  const hasOrders = totalOrders > 0;

  // Build queue in priority order (first match = highest)
  const badge = t('nextStep.badge', 'الخطوة التالية');
  const steps: NextStep[] = [];

  if (unfulfilled > 0) steps.push({
    id: 'unfulfilled',
    badge, Icon: ShoppingCart,
    title: t('nextStep.unfulfilledTitle', 'طلبات تنتظر التجهيز'),
    body: t('nextStep.unfulfilledBody', 'بادر بمعالجتها قبل ما يبرد الحماس.'),
    metric: <><span className="num">{unfulfilled}</span> <span className="text-[11px] opacity-70">{t('nextStep.unfulfilledUnit', 'طلب')}</span></>,
    cta: t('nextStep.ctaFulfill', 'جهّز الطلبات'),
    path: '/orders',
  });

  if (lowStock > 0) steps.push({
    id: 'low-stock',
    badge, Icon: Warehouse,
    title: t('nextStep.lowStockTitle', 'مخزون على وشك النفاد'),
    body: t('nextStep.lowStockBody', 'جدّد الكميات قبل ما تفوتك مبيعات.'),
    metric: <><span className="num">{lowStock}</span> <span className="text-[11px] opacity-70">{t('nextStep.lowStockUnit', 'منتج')}</span></>,
    cta: t('nextStep.ctaRestock', 'حدّث المخزون'),
    path: '/inventory',
  });

  if (cartCount > 0 && cartValue > 0) steps.push({
    id: 'carts',
    badge, Icon: ShoppingBasket,
    title: t('nextStep.cartsTitle', 'سلات مهجورة تنتظر'),
    body: t('nextStep.cartsBody', 'رسالة واحدة ترجعهم لإكمال الشراء.'),
    metric: (
      <>
        <span className="num">{cartCount}</span>{' '}
        <span className="text-[11px] opacity-70">•</span>{' '}
        <span className="num">{formatValue(cartValue)}</span>{' '}
        <span className="text-[11px] opacity-70">ر.س</span>
      </>
    ),
    cta: t('nextStep.ctaRecover', 'استرجع السلات'),
    path: '/carts',
  });

  if (productCount === 0) steps.push({
    id: 'no-products',
    badge, Icon: ShoppingBag,
    title: t('nextStep.noProductsTitle', 'أضف أول منتج'),
    body: t('nextStep.noProductsBody', 'بداية كل متجر: منتج واحد على الرف.'),
    cta: t('nextStep.ctaAddProduct', 'إضافة منتج'),
    path: '/products/create',
  });

  if (!hasLogo) steps.push({
    id: 'no-logo',
    badge, Icon: ImageIcon,
    title: t('nextStep.noLogoTitle', 'أضف شعار متجرك'),
    body: t('nextStep.noLogoBody', 'الشعار يعطي انطباع احترافي من أول زيارة.'),
    cta: t('nextStep.ctaAddLogo', 'رفع الشعار'),
    path: '/settings',
  });

  const visibleSteps = steps.filter(s => !hidden.includes(s.id));
  const queue = visibleSteps.slice(0, 3);

  return (
    <div className="relative">
      {queue.length === 0
        ? <ShareStoreCard storeUrl={storeUrl} hasOrders={hasOrders} />
        : <NextStepCarousel items={queue} onGo={go} />
      }

      <button
        type="button"
        onClick={() => setPrefsOpen(v => !v)}
        aria-label={t('nextStep.editPrefs', 'تعديل الاقتراحات')}
        title={t('nextStep.editPrefs', 'تعديل الاقتراحات')}
        className="absolute top-2 end-2 z-10 inline-flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
      >
        <SlidersHorizontal className="h-3 w-3" />
      </button>

      {prefsOpen && (
        <NextStepPrefsPanel
          hidden={hidden}
          onToggle={toggleHidden}
          onClose={() => setPrefsOpen(false)}
        />
      )}
    </div>
  );
}

function NextStepPrefsPanel({ hidden, onToggle, onClose }:
{ hidden: string[]; onToggle: (id: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute end-2 top-9 z-20 w-56 rounded-xl border border-border-default bg-bg-secondary p-2 shadow-lg"
    >
      <div className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-text-muted">
        {t('nextStep.prefsTitle', 'اعرض هذه الاقتراحات')}
      </div>
      <ul className="mt-1 space-y-0.5">
        {NEXT_STEP_KINDS.map(k => {
          const isHidden = hidden.includes(k.id);
          return (
            <li key={k.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[12px] text-text-secondary hover:bg-bg-hover hover:text-text-primary">
                <input
                  type="checkbox"
                  checked={!isHidden}
                  onChange={() => onToggle(k.id)}
                  className="h-3.5 w-3.5 accent-current"
                />
                <span className="flex-1">{t(k.labelKey, k.fallback)}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Single step descriptor used by the carousel. */
interface NextStep {
  id: string;
  badge: string;
  Icon: LucideIcon;
  title: string;
  body: string;
  metric?: React.ReactNode;
  cta: string;
  path: string;
}

/** Tiny hook - respects `prefers-reduced-motion`. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(m.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    m.addEventListener?.('change', onChange);
    return () => m.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

/** Rotator: 5s dwell equal, +2s bonus on first card's first appearance only.
 *  Pauses on hover; disables auto-rotate for reduced-motion users. */
function NextStepCarousel({ items, onGo }: { items: NextStep[]; onGo: (p: string) => void }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const firstCycleRef = useRef(true);
  const reducedMotion = usePrefersReducedMotion();
  const n = items.length;
  const singleton = n <= 1;

  // Clamp index when queue shrinks (e.g. merchant resolves a rule)
  useEffect(() => {
    if (index >= n) setIndex(0);
  }, [n, index]);

  useEffect(() => {
    if (singleton || reducedMotion || paused) return;
    const bonus = firstCycleRef.current && index === 0 ? 2000 : 0;
    const id = window.setTimeout(() => {
      setIndex(i => {
        const next = (i + 1) % n;
        if (next === 0) firstCycleRef.current = false;
        return next;
      });
    }, 5000 + bonus);
    return () => window.clearTimeout(id);
  }, [index, n, paused, reducedMotion, singleton]);

  const step = items[Math.min(index, n - 1)];

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div key={index} className="animate-next-step">
        <NextStepShell
          badge={step.badge}
          Icon={step.Icon}
          title={step.title}
          body={step.body}
          metric={step.metric}
          cta={step.cta}
          onCta={() => onGo(step.path)}
        />
      </div>

      {!singleton && (
        <div className="mt-2 flex items-center justify-center gap-1.5" role="tablist" aria-label="next-step">
          {items.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === index}
              onClick={() => { setIndex(i); firstCycleRef.current = false; }}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === index ? 18 : 6,
                background: i === index ? '#D2E9FE' : 'rgba(255,255,255,0.18)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared shell used by every next-step variant — themed via CSS in globals.css. */
function NextStepShell({ badge, Icon, title, body, metric, cta, onCta }:
{ badge: string; Icon: LucideIcon; title: string; body: string; metric?: React.ReactNode; cta: string; onCta: () => void }) {
  return (
    <div className="next-step-shell relative overflow-hidden rounded-2xl p-4">
      <svg
        viewBox="0 0 200 160"
        aria-hidden
        className="next-step-deco pointer-events-none absolute -end-6 -bottom-6 h-36 w-36"
      >
        <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 120 C 40 60, 90 60, 110 100 S 180 140, 190 80" />
          <path d="M30 90 L 50 70 L 70 92 L 92 66" />
          <circle cx="140" cy="50" r="14" />
          <circle cx="140" cy="50" r="6" />
          <path d="M60 140 Q 100 110, 160 130" />
          <path d="M80 30 L 88 38 L 80 46 L 72 38 Z" />
          <path d="M170 100 l 8 8 m 0 -8 l -8 8" />
        </g>
      </svg>

      <div className="relative">
        <div className="next-step-badge inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold">
          <Icon className="h-3 w-3" />
          {badge}
        </div>

        <div className="next-step-title mt-2 text-[13px] font-bold leading-snug">
          {title}
        </div>
        <div className="next-step-body mt-1 text-[11.5px] leading-relaxed">
          {body}
        </div>

        {metric && (
          <div className="next-step-metric mt-2.5 text-[22px] font-bold leading-none" style={{ letterSpacing: '-0.015em' }}>
            {metric}
          </div>
        )}

        <button
          onClick={onCta}
          className="next-step-cta mt-3 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors"
        >
          {cta}
          <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function formatValue(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'M';
  if (n >= 1_000)     return (n / 1_000).toLocaleString('en-US',     { maximumFractionDigits: 1 }) + 'K';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Dark gradient card - share-your-store helper. Copy switches based on
 *  whether the store has ever had an order so we don't keep nudging mature
 *  stores about their "first order". */
function ShareStoreCard({ storeUrl, hasOrders }: { storeUrl: string | null; hasOrders: boolean }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const prettyUrl = storeUrl ? storeUrl.replace(/^https?:\/\//, '') : '';

  const copy = async () => {
    if (!storeUrl) return;
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  const whatsapp = () => {
    if (!storeUrl) return;
    const msg = encodeURIComponent(`${t('share.whatsappMsg', 'تفضّل زيارة متجري')}: ${storeUrl}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-4"
      style={{
        background: 'linear-gradient(155deg, #1E2B42, #0a1224 55%, #010101)',
        border: '1px solid rgba(210,233,254,0.10)',
      }}
    >
      {/* Faint hand-drawn motif */}
      <svg
        viewBox="0 0 200 160"
        aria-hidden
        className="pointer-events-none absolute -end-6 -bottom-6 h-36 w-36"
        style={{ opacity: 0.12, color: '#D2E9FE' }}
      >
        <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 120 C 40 60, 90 60, 110 100 S 180 140, 190 80" />
          <path d="M30 90 L 50 70 L 70 92 L 92 66" />
          <circle cx="140" cy="50" r="14" />
          <circle cx="140" cy="50" r="6" />
          <path d="M60 140 Q 100 110, 160 130" />
          <path d="M80 30 L 88 38 L 80 46 L 72 38 Z" />
          <path d="M170 100 l 8 8 m 0 -8 l -8 8" />
        </g>
      </svg>

      <div className="relative">
        <div className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold"
             style={{ background: 'rgba(210,233,254,0.10)', color: '#D2E9FE' }}>
          <Share2 className="h-3 w-3" />
          {t('share.badge', 'رابط المتجر')}
        </div>
        <div className="mt-2 text-[13px] font-bold leading-snug" style={{ color: '#E5E7EB' }}>
          {hasOrders
            ? t('share.titleReturning', 'وصّل متجرك لزبائن جدد')
            : t('share.title', 'شارك متجرك مع زبائنك')}
        </div>

        {storeUrl && (
          <>
            <div
              className="mt-3 truncate rounded-lg px-2.5 py-1.5 text-[11px] mono"
              style={{ background: 'rgba(14,19,32,0.6)', border: '1px solid rgba(210,233,254,0.08)', color: '#CBD5E1', direction: 'ltr' }}
              title={storeUrl}
            >
              {prettyUrl}
            </div>
            <div className="mt-2.5 flex items-center gap-1.5">
              <button
                onClick={copy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors"
                style={{ background: '#D2E9FE', color: '#0A2248' }}
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? t('share.copied', 'تم النسخ') : t('share.copy', 'نسخ')}
              </button>
              <button
                onClick={whatsapp}
                title="WhatsApp"
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors"
                style={{ background: 'rgba(210,233,254,0.08)', color: '#D2E9FE' }}
              >
                <WhatsAppIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.197.296-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413"/>
    </svg>
  );
}
